<template>
  <section id="rehber" class="section section--soft seo-knowledge">
    <div class="container">
      <div class="seo-knowledge__header">
        <p class="eyebrow">Detaylı Yazılım & Sektör Rehberi</p>
        <h2 class="section-title">Kurumsal Yazılım, B2B, CRM ve Entegrasyon Bilgi Bankası</h2>
        <p class="section-lead">
          SC Dijital olarak geliştirdiğimiz CRM sistemleri, B2B bayi portalları, etkinlik biletleme altyapıları,
          e-ticaret, mobil uygulamalar, sanal POS, muhasebe (Uyumsoft, Paraşüt, Logo) ve AloTech entegrasyonlarına dair
          mimari detaylar, teknik kılavuzlar ve sektörel çözümler.
        </p>

        <!-- Arama ve Filtreleme -->
        <div class="seo-knowledge__search-bar">
          <input
            v-model="searchTerm"
            type="search"
            class="seo-knowledge__input"
            placeholder="Rehberde ara: CRM, B2B, Uyumsoft, AloTech, Koltuk Seçimi, Sanal POS, Kayseri..."
            aria-label="Yazılım rehberinde ara"
          />
          <div class="seo-knowledge__pills">
            <button
              v-for="cat in categories"
              :key="cat.id"
              type="button"
              class="seo-knowledge__pill"
              :class="{ 'is-active': activeCategory === cat.id }"
              @click="activeCategory = cat.id"
            >
              {{ cat.name }}
            </button>
          </div>
        </div>
      </div>

      <!-- Konu Başlıkları Akordiyon / Grid Listesi -->
      <div class="seo-knowledge__content">
        <article
          v-for="item in filteredArticles"
          :key="item.id"
          class="seo-card"
        >
          <div class="seo-card__tag">{{ item.category }}</div>
          <h3 class="seo-card__title">{{ item.title }}</h3>
          <p class="seo-card__summary">{{ item.summary }}</p>

          <div class="seo-card__body">
            <div v-for="(sec, sIdx) in item.sections" :key="sIdx" class="seo-card__sec">
              <h4>{{ sec.heading }}</h4>
              <p>{{ sec.text }}</p>
              <ul v-if="sec.bullets && sec.bullets.length">
                <li v-for="(b, bIdx) in sec.bullets" :key="bIdx">
                  <strong>{{ b.label }}:</strong> {{ b.desc }}
                </li>
              </ul>
            </div>
          </div>
        </article>
      </div>

      <!-- Kapsamlı Sektörel Şehir & Endüstri Haritası -->
      <div class="seo-knowledge__industry-grid">
        <div class="seo-industry-box">
          <h3>Türkiye Geneli 81 İl Sanayi ve Ticaret İçin Özel Yazılım Çözümleri</h3>
          <p>
            SC Dijital, Kayseri merkezli yazılım mimarisiyle İstanbul, Ankara, İzmir, Bursa, Gaziantep, Konya, Kocaeli,
            Adana, Denizli, Antalya, Eskişehir, Samsun, Trabzon, Tekirdağ, Manisa, Sakarya, Balıkesir, Mersin ve Türkiye'nin
            tüm 81 ilindeki imalatçı, toptancı ve perakendeci işletmelerin iş süreçlerini dijitalleştirmektedir.
            Masaüstü ve web hibrit mimarisi sayesinde internet dalgalanmalarından etkilenmeyen, yerel sunucuda ya da bulutta
            çalışabilen güvenli sistemler kuruyoruz.
          </p>
          <div class="seo-tags">
            <span v-for="city in featuredCities" :key="city" class="seo-tag">{{ city }} Yazılım Çözümleri</span>
          </div>
        </div>

        <div class="seo-industry-box">
          <h3>Sektörlere Özel CRM, B2B ve Üretim (BOM) Entegrasyonları</h3>
          <p>
            Her sektörün sipariş, teklif, iskonto ve üretim dinamikleri farklıdır. Standart paket yazılımların yetersiz
            kaldığı noktada, işletmenizin gerçek saha alışkanlıklarına ve muhasebe programınıza (Uyumsoft, Paraşüt, Logo)
            birebir uyum sağlayan esnek modüller kodluyoruz:
          </p>
          <div class="seo-tags">
            <span v-for="ind in featuredIndustries" :key="ind" class="seo-tag">{{ ind }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, ref } from "vue";
import { seoArticlesData, seoCities, seoIndustries } from "@/data/seoArticles";

export default {
  name: "SeoKnowledgeSection",
  setup() {
    const searchTerm = ref("");
    const activeCategory = ref("all");

    const categories = [
      { id: "all", name: "Tüm Konular" },
      { id: "crm", name: "CRM Sistemleri" },
      { id: "b2b", name: "B2B Bayi Portalları" },
      { id: "events", name: "Etkinlik & Biletleme" },
      { id: "integrations", name: "POS & Muhasebe" },
      { id: "mobile", name: "Mobil & ERP" },
      { id: "alotech", name: "AloTech & CTI" },
    ];

    const filteredArticles = computed(() => {
      const q = searchTerm.value.trim().toLowerCase();
      return seoArticlesData.filter((art) => {
        const matchesCat = activeCategory.value === "all" || art.categoryId === activeCategory.value;
        if (!matchesCat) return false;
        if (!q) return true;
        const inTitle = art.title.toLowerCase().includes(q);
        const inSummary = art.summary.toLowerCase().includes(q);
        const inBody = art.sections.some(
          (s) =>
            s.heading.toLowerCase().includes(q) ||
            s.text.toLowerCase().includes(q) ||
            (s.bullets && s.bullets.some((b) => b.label.toLowerCase().includes(q) || b.desc.toLowerCase().includes(q)))
        );
        return inTitle || inSummary || inBody;
      });
    });

    return {
      searchTerm,
      activeCategory,
      categories,
      filteredArticles,
      featuredCities: seoCities,
      featuredIndustries: seoIndustries,
    };
  },
};
</script>

<style scoped>
.seo-knowledge {
  border-top: 1px solid var(--line);
  padding: 80px 0;
}
.seo-knowledge__header {
  text-align: center;
  max-width: 960px;
  margin: 0 auto 48px;
}
.seo-knowledge__search-bar {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}
.seo-knowledge__input {
  width: 100%;
  max-width: 640px;
  padding: 14px 20px;
  font-size: 15px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #ffffff;
  color: var(--ink);
  outline: none;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.seo-knowledge__input:focus {
  border-color: var(--brand);
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
}
.seo-knowledge__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}
.seo-knowledge__pill {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #ffffff;
  color: var(--ink-700);
  cursor: pointer;
  transition: all 0.2s ease;
}
.seo-knowledge__pill:hover {
  border-color: var(--brand-400);
  color: var(--brand);
}
.seo-knowledge__pill.is-active {
  background: var(--brand);
  color: #ffffff;
  border-color: var(--brand);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
}

.seo-knowledge__content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 28px;
  margin-bottom: 56px;
}
@media (max-width: 768px) {
  .seo-knowledge__content {
    grid-template-columns: 1fr;
  }
}

.seo-card {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 32px 28px;
  box-shadow: 0 10px 30px -15px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
}
.seo-card__tag {
  align-self: flex-start;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--brand);
  background: var(--brand-50);
  padding: 4px 10px;
  border-radius: 6px;
  margin-bottom: 14px;
}
.seo-card__title {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.3;
  margin: 0 0 12px;
  color: var(--ink);
}
.seo-card__summary {
  font-size: 14px;
  color: var(--ink-500);
  line-height: 1.6;
  margin: 0 0 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--ink-100);
}
.seo-card__body {
  font-size: 13.5px;
  color: var(--ink-700);
  line-height: 1.65;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.seo-card__sec h4 {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 6px;
}
.seo-card__sec p {
  margin: 0 0 8px;
}
.seo-card__sec ul {
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.seo-card__sec li strong {
  color: var(--ink);
}

.seo-knowledge__industry-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  margin-top: 24px;
}
@media (max-width: 900px) {
  .seo-knowledge__industry-grid {
    grid-template-columns: 1fr;
  }
}
.seo-industry-box {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 32px 28px;
  box-shadow: 0 8px 24px -12px rgba(15, 23, 42, 0.05);
}
.seo-industry-box h3 {
  font-size: 18px;
  font-weight: 800;
  margin: 0 0 12px;
  color: var(--ink);
}
.seo-industry-box p {
  font-size: 14px;
  color: var(--ink-700);
  line-height: 1.65;
  margin: 0 0 20px;
}
.seo-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.seo-tag {
  font-size: 12px;
  font-weight: 600;
  background: var(--ink-100);
  color: var(--ink-700);
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid transparent;
  transition: all 0.2s;
}
.seo-tag:hover {
  background: var(--brand-50);
  color: var(--brand);
  border-color: var(--brand-100);
}
</style>
